import { useCallback, useRef, useState } from 'react'

/**
 * Hook allowing to debounce call of the passed callback function
 */
export default function useDebouncedCallback<T>(defaultValue: T, callback: (value: T) => void, delay?: number): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(defaultValue)
  const timeout = useRef<ReturnType<typeof setTimeout>>()

  const setValueWithDebounce = useCallback(
    currentValue => {
      setValue(currentValue)
      if (timeout.current) {
        clearTimeout(timeout.current)
      }
      if (delay === 0) {
        callback(currentValue)
        timeout.current = undefined
      } else {
        timeout.current = setTimeout(() => callback(currentValue), delay || 500)
      }
      return () => {
        if (timeout.current) {
          clearTimeout(timeout.current)
        }
      }
    },
    [delay, callback],
  )

  return [value, setValueWithDebounce]
}
